<template>
  <b-nav-item-dropdown
    class="dropdown-notification mr-25"
    menu-class="dropdown-menu-media"
    right
  >
    <template #button-content>
      <feather-icon
        :badge="unreadCount"
        :badge-classes="badgeClasses"
        :class="bellIconClass"
        icon="BellIcon"
        size="21"
      />
    </template>

    <!-- Header -->
    <li class="dropdown-menu-header">
      <div class="dropdown-header d-flex">
        <h4 class="notification-title mb-0 mr-auto">
          Agendamentos
        </h4>
        <b-badge
          v-if="unreadCount > 0"
          pill
          variant="light-primary"
        >
          {{ unreadCount }} {{ unreadCount === 1 ? 'Novo' : 'Novos' }}
        </b-badge>
      </div>
    </li>

    <!-- Notifications -->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="scrollable-container media-list scroll-area"
      tagname="li"
    >
      <template v-if="agendamentos.length > 0">
        <div
          v-for="agendamento in agendamentos"
          :key="agendamento.id"
          class="d-flex notification-item"
          :class="[
            { 'notification-unread': !agendamento.lido },
            getProximidadeClass(agendamento.dataAgendamento)
          ]"
        >
          <b-link
            class="flex-grow-1"
            @click="handleNotificationClick(agendamento)"
          >
            <b-media>
              <template #aside>
                <b-avatar
                  size="32"
                  :variant="getAvatarVariant(agendamento.dataAgendamento)"
                >
                  <feather-icon icon="CalendarIcon" />
                </b-avatar>
              </template>
              <p class="media-heading mb-0">
                <span class="font-weight-bolder">
                  {{ agendamento.titulo }}
                </span>
                <span
                  class="badge ml-1"
                  :class="getBadgeVariant(agendamento.dataAgendamento)"
                >
                  {{ getProximidadeLabel(agendamento.dataAgendamento) }}
                </span>
              </p>
              <small class="notification-text d-block">{{ agendamento.cliente }}</small>
              <small
                v-if="agendamento.descricao"
                class="notification-text d-block text-dark"
              >
                {{ agendamento.descricao }}
              </small>
              <small class="text-muted">{{ formatarData(agendamento.dataAgendamento) }}</small>
            </b-media>
          </b-link>

          <!-- Botão de marcar como lido -->
          <div class="d-flex align-items-center pr-1">
            <b-button
              v-if="!agendamento.lido"
              variant="flat-success"
              size="sm"
              class="btn-icon"
              @click.stop="marcarComoLido(agendamento.id)"
            >
              <feather-icon
                icon="CheckIcon"
                size="16"
              />
            </b-button>
          </div>
        </div>
      </template>

      <!-- Mensagem quando não há notificações -->
      <div
        v-else
        class="p-2 text-center"
      >
        <small class="text-muted">Nenhum agendamento pendente</small>
      </div>
    </vue-perfect-scrollbar>

    <!-- Footer -->
    <li
      v-if="agendamentos.length > 0"
      class="dropdown-menu-footer"
    >
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        block
        @click="marcarTodosComoLido"
      >
        Marcar todos como lido
      </b-button>
    </li>
  </b-nav-item-dropdown>
</template>

<script>
import {
  BNavItemDropdown, BBadge, BMedia, BLink, BAvatar, BButton,
} from 'bootstrap-vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import Ripple from 'vue-ripple-directive'
import { formatDistance, differenceInMinutes } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import router from '@/router'
import notificacoesMixin from '@/mixins/notificacoes'

export default {
  components: {
    BNavItemDropdown,
    BBadge,
    BMedia,
    BLink,
    BAvatar,
    VuePerfectScrollbar,
    BButton,
  },
  directives: {
    Ripple,
  },
  mixins: [notificacoesMixin],
  data() {
    return {
      perfectScrollbarSettings: {
        maxScrollbarLength: 60,
        wheelPropagation: false,
      },
    }
  },
  computed: {
    // Transformar notificações da API em formato compatível com o template
    agendamentos() {
      return this.notificacoes
        // Filtrar apenas notificações NÃO lidas
        .filter(notif => !notif.read_at)
        .map(notif => ({
          id: notif.id,
          titulo: this.getTituloNotificacao(notif),
          cliente: this.getClienteInfo(notif),
          descricao: notif.data && notif.data.descricao ? notif.data.descricao : null,
          data: notif.created_at,
          dataAgendamento: notif.data && notif.data.data_agendamento ? notif.data.data_agendamento : notif.created_at,
          clienteId: notif.data && notif.data.cliente_id ? notif.data.cliente_id : null,
          negociacaoId: notif.data && notif.data.agendamento_id ? notif.data.agendamento_id : notif.data.cliente_id || notif.id,
          lido: !!notif.read_at,
          notificacao: notif, // manter referência original
        }))
        // Ordenar por data_agendamento (mais próximo no topo)
        .sort((a, b) => {
          const dataA = new Date(a.dataAgendamento).getTime()
          const dataB = new Date(b.dataAgendamento).getTime()
          return dataA - dataB // Mais próximo (menor timestamp) fica primeiro
        })
    },

    /**
     * Verifica se há agendamentos críticos ou urgentes
     */
    temAgendamentoProximo() {
      return this.agendamentos.some(agendamento => {
        const minutos = this.getMinutosRestantes(agendamento.dataAgendamento)
        return minutos >= 0 && minutos <= 30 // Crítico (0-5) ou urgente (6-15) ou aviso (16-30)
      })
    },

    /**
     * Retorna classe CSS para o ícone do bell
     */
    bellIconClass() {
      if (this.temAgendamentoProximo) {
        return 'text-danger animate-bell'
      }
      return 'text-body'
    },

    /**
     * Retorna classe CSS para o badge do bell
     */
    badgeClasses() {
      if (this.temAgendamentoProximo) {
        return 'bg-danger animate-badge'
      }
      return 'bg-primary'
    },
  },
  methods: {
    getTituloNotificacao(notif) {
      const tipo = notif.data && notif.data.tipo ? notif.data.tipo : 'AGENDAMENTO'
      const tiposLabels = {
        NEGOCIACAO: 'Negociação agendada',
        RETORNO: 'Retorno agendado',
        FOLLOW_UP: 'Follow-up agendado',
        COBRANCA: 'Cobrança agendada',
      }
      return tiposLabels[tipo] || 'Novo agendamento'
    },

    getClienteInfo(notif) {
      const clienteId = notif.data && notif.data.cliente_id ? notif.data.cliente_id : null
      const tipo = notif.data && notif.data.tipo ? notif.data.tipo : ''
      const atendenteNome = notif.data && notif.data.atendente_nome ? notif.data.atendente_nome : 'Sistema'

      if (clienteId) {
        return `Cliente #${clienteId} • ${atendenteNome}`
      }
      return `${tipo} • ${atendenteNome}`
    },

    formatarData(data) {
      try {
        return formatDistance(new Date(data), new Date(), {
          addSuffix: true,
          locale: ptBR,
        })
      } catch (error) {
        return 'Data inválida'
      }
    },

    async handleNotificationClick(agendamento) {
      // Marca como lido ao clicar
      await this.marcarComoLida(agendamento.id)

      // Navega para a página de negociação usando o cliente_id
      router.push({ name: 'negociacao-cliente', params: { id: agendamento.clienteId } })
    },

    /**
     * Calcula minutos restantes até o agendamento
     */
    getMinutosRestantes(dataAgendamento) {
      try {
        return differenceInMinutes(new Date(dataAgendamento), new Date())
      } catch (error) {
        return -1
      }
    },

    /**
     * Retorna classe CSS baseada na proximidade do agendamento
     */
    getProximidadeClass(dataAgendamento) {
      const minutos = this.getMinutosRestantes(dataAgendamento)

      if (minutos < 0) {
        return 'notification-expired'
      }
      if (minutos <= 5) {
        return 'notification-critical'
      }
      if (minutos <= 15) {
        return 'notification-urgent'
      }
      if (minutos <= 30) {
        return 'notification-warning'
      }
      return 'notification-normal'
    },

    /**
     * Retorna variante do avatar baseada na proximidade
     */
    getAvatarVariant(dataAgendamento) {
      const minutos = this.getMinutosRestantes(dataAgendamento)

      if (minutos < 0) {
        return 'light-dark'
      }
      if (minutos <= 5) {
        return 'light-danger'
      }
      if (minutos <= 15) {
        return 'light-warning'
      }
      if (minutos <= 30) {
        return 'light-info'
      }
      return 'light-primary'
    },

    /**
     * Retorna classe do badge indicando proximidade
     */
    getBadgeVariant(dataAgendamento) {
      const minutos = this.getMinutosRestantes(dataAgendamento)

      if (minutos < 0) {
        return 'badge-secondary'
      }
      if (minutos <= 5) {
        return 'badge-danger'
      }
      if (minutos <= 15) {
        return 'badge-warning'
      }
      if (minutos <= 30) {
        return 'badge-info'
      }
      return 'badge-primary'
    },

    /**
     * Retorna label indicando proximidade do agendamento
     */
    getProximidadeLabel(dataAgendamento) {
      const minutos = this.getMinutosRestantes(dataAgendamento)

      if (minutos < 0) {
        return 'Pendente'
      }
      if (minutos <= 5) {
        return '🚨 Agora!'
      }
      if (minutos <= 15) {
        return '⚠️ Em breve'
      }
      if (minutos <= 30) {
        return '⏰ Próximo'
      }
      return '📅 Agendado'
    },

    /**
     * Marca notificação como lida e remove do dropdown
     */
    async marcarComoLido(notificationId) {
      try {
        await this.marcarComoLida(notificationId)
        // Após marcar como lida, a notificação será filtrada automaticamente
        // pois o computed agendamentos filtra por !notif.read_at
      } catch (error) {
        if (this.$bvToast) {
          this.$bvToast.toast('Erro ao marcar como lida', {
            title: 'Erro',
            variant: 'danger',
            solid: true,
          })
        }
      }
    },

    /**
     * Marca todas as notificações como lidas
     */
    async marcarTodosComoLido() {
      try {
        await this.marcarTodasComoLidas()
        // Após marcar todas como lidas, o dropdown ficará vazio
        // pois o computed agendamentos filtra por !notif.read_at
      } catch (error) {
        // Erro ao marcar todas como lidas
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.notification-item {
  padding: 0.5rem 1rem;
  transition: background-color 0.2s, border-left 0.2s;
  border-left: 3px solid transparent;

  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  &.notification-unread {
    background-color: rgba(115, 103, 240, 0.05);
  }

  // Estilos baseados na proximidade
  &.notification-critical {
    border-left-color: #da534f;
    background-color: rgba(218, 83, 79, 0.05);

    &:hover {
      background-color: rgba(218, 83, 79, 0.08);
    }
  }

  &.notification-urgent {
    border-left-color: #f4a460;
    background-color: rgba(244, 164, 96, 0.05);

    &:hover {
      background-color: rgba(244, 164, 96, 0.08);
    }
  }

  &.notification-warning {
    border-left-color: #3498db;
    background-color: rgba(52, 152, 219, 0.05);

    &:hover {
      background-color: rgba(52, 152, 219, 0.08);
    }
  }

  &.notification-normal {
    border-left-color: #7367f0;

    &:hover {
      background-color: rgba(0, 0, 0, 0.02);
    }
  }

  &.notification-expired {
    border-left-color: #95989a;
    opacity: 0.6;
    background-color: rgba(149, 152, 154, 0.05);

    &:hover {
      background-color: rgba(149, 152, 154, 0.08);
    }
  }
}

.media-heading {
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .badge {
    font-size: 0.7rem;
    white-space: nowrap;
  }
}

.notification-text {
  line-height: 1.3;
}

.btn-icon {
  padding: 0.25rem 0.5rem;
}

/* Animações para alertas */
@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-3px);
  }
  75% {
    transform: translateX(3px);
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.animate-bell {
  animation: shake 0.5s ease-in-out 0s infinite;
}

.animate-badge {
  animation: pulse 1.5s ease-in-out 0s infinite;
}
</style>
